import axios from 'axios';

class GeocodingMonitoringService {
  constructor() {
    this.baseURL = process.env.REACT_APP_API_URL || '';
  }

  /**
   * Récupérer les statistiques en temps réel
   */
  async getStats() {
    try {
      const response = await axios.get(`${this.baseURL}/api/v1/geocoding/monitoring/stats`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des stats:', error);
      throw error;
    }
  }

  /**
   * Récupérer le statut des providers
   */
  async getProvidersStatus() {
    try {
      const response = await axios.get(`${this.baseURL}/api/v1/geocoding/monitoring/providers/status`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération du statut des providers:', error);
      throw error;
    }
  }

  /**
   * Activer / Désactiver un provider côté backend admin
   */
  async toggleProvider(name, enabled) {
    try {
      const response = await axios.post(`${this.baseURL}/api/v1/geocoding/admin/providers/toggle`, { name, enabled });
      return response.data;
    } catch (error) {
      console.error('Erreur lors du toggle du provider:', error);
      throw error;
    }
  }

  /**
   * Afficher les stats dans la console du serveur
   */
  async displayStatsInConsole() {
    try {
      const response = await axios.get(`${this.baseURL}/api/v1/geocoding/monitoring/stats/console`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de l\'affichage des stats en console:', error);
      throw error;
    }
  }

  /**
   * Réinitialiser les statistiques
   */
  async resetStats() {
    try {
      const response = await axios.post(`${this.baseURL}/api/v1/geocoding/monitoring/stats/reset`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la réinitialisation des stats:', error);
      throw error;
    }
  }

  /**
   * Tester l'API de géocodage
   */
  async testGeocoding(address = 'Paris, France') {
    try {
      const response = await axios.get(`${this.baseURL}/api/v1/geocoding/monitoring/test`, {
        params: { address }
      });
      return response.data;
    } catch (error) {
      console.error('Erreur lors du test de géocodage:', error);
      throw error;
    }
  }

  /**
   * Récupérer toutes les données de monitoring en une seule fois
   */
  async getAllMonitoringData() {
    try {
      const [statsResponse, providersResponse] = await Promise.all([
        this.getStats(),
        this.getProvidersStatus()
      ]);

      return {
        stats: statsResponse.success ? statsResponse.data : null,
        providers: providersResponse.success ? providersResponse.data.providers : [],
        errors: {
          stats: !statsResponse.success ? statsResponse.error : null,
          providers: !providersResponse.success ? providersResponse.error : null
        }
      };
    } catch (error) {
      console.error('Erreur lors de la récupération des données de monitoring:', error);
      throw error;
    }
  }

  /**
   * Surveiller les changements en temps réel
   */
  createRealTimeMonitor(callback, interval = 5000) {
    const monitor = {
      isRunning: false,
      intervalId: null,
      statsIntervalId: null,
      providersIntervalId: null,
      
      start: () => {
        if (monitor.isRunning) return;
        
        monitor.isRunning = true;
        
        // Appeler immédiatement une fois
        monitor.fetchData();
        
        // Stats : mise à jour toutes les 5 secondes (rapide)
        monitor.statsIntervalId = setInterval(async () => {
          try {
            const statsResponse = await this.getStats();
            const stats = statsResponse.success ? statsResponse.data : null;
            if (stats) {
              callback({ stats, providers: null, errors: {} });
            }
          } catch (error) {
            callback({ error: error.message });
          }
        }, interval);
        
        // Providers : mise à jour toutes les 15 secondes (moins fréquent)
        monitor.providersIntervalId = setInterval(async () => {
          try {
            const providersResponse = await this.getProvidersStatus();
            const providers = providersResponse.success ? providersResponse.data.providers : [];
            if (providers) {
              callback({ stats: null, providers, errors: {} });
            }
          } catch (error) {
            callback({ error: error.message });
          }
        }, interval * 3); // 15 secondes
      },
      
      fetchData: async () => {
        try {
          const data = await this.getAllMonitoringData();
          callback(data);
        } catch (error) {
          callback({ error: error.message });
        }
      },
      
      stop: () => {
        if (monitor.statsIntervalId) {
          clearInterval(monitor.statsIntervalId);
          monitor.statsIntervalId = null;
        }
        if (monitor.providersIntervalId) {
          clearInterval(monitor.providersIntervalId);
          monitor.providersIntervalId = null;
        }
        monitor.isRunning = false;
      },
      
      restart: () => {
        monitor.stop();
        monitor.start();
      }
    };

    return monitor;
  }

  /**
   * Formater les données pour l'affichage
   */
  formatStatsForDisplay(stats) {
    if (!stats) return null;

    return {
      ...stats,
      formatted: {
        timestamp: new Date(stats.timestamp).toLocaleString('fr-FR'),
        providers: Object.entries(stats.providers).map(([name, data]) => ({
          name: name.toUpperCase(),
          ...data,
          successRateNum: parseFloat(data.successRate),
          avgResponseTimeNum: parseFloat(data.avgResponseTime),
          status: data.isActive ? 'active' : 'inactive'
        })),
        cache: {
          ...stats.cache,
          hitRateNum: parseFloat(stats.cache.hitRate),
          total: stats.cache.hits + stats.cache.misses
        },
        summary: {
          ...stats.summary,
          successRateNum: parseFloat(stats.summary.overallSuccessRate)
        }
      }
    };
  }

  /**
   * Calculer les métriques de performance
   */
  calculatePerformanceMetrics(stats) {
    if (!stats || !stats.providers) return null;

    const providers = Object.values(stats.providers);
    const totalRequests = providers.reduce((sum, p) => sum + p.requests, 0);
    const totalSuccess = providers.reduce((sum, p) => sum + p.success, 0);
    const totalErrors = providers.reduce((sum, p) => sum + p.errors, 0);

    return {
      availability: totalRequests > 0 ? (totalSuccess / totalRequests * 100).toFixed(2) : 0,
      reliability: totalRequests > 0 ? ((totalRequests - totalErrors) / totalRequests * 100).toFixed(2) : 0,
      activeProviders: providers.filter(p => p.isActive).length,
      totalProviders: providers.length,
      cacheEfficiency: stats.cache ? parseFloat(stats.cache.hitRate) : 0
    };
  }
}

// Instance singleton
const geocodingMonitoringService = new GeocodingMonitoringService();

export default geocodingMonitoringService;
